import { Card, CardContent, Grid } from '@mui/material'
import PropTypes from 'prop-types'
import SelectionTable from 'components/datatable/SelectionTable'
import React, { useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import { compact, difference, isNil } from 'lodash'
import useListIndexed from 'utils/customHook/useListIndexed'
import Input from 'components/forms/Input'
import SelectionSelect from 'components/forms/specific/SelectionSelect'
import { shallowEqual, useSelector } from 'react-redux'
import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import Select from 'components/forms/Select'
import useDebounce from 'utils/customHook/useDebounce'
import { SELECTION } from 'quality/constants/QualityConstants'
import { searchAllCharacters } from 'utils/StringUtil'
import Checkbox from 'components/forms/Checkbox'
import { authorizeFilter } from 'utils/HabilitationUtil'
import { SHOW_INSEE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'

const SimpleSelectionTable = ({
    onChange = () => { },

    listData = [],
    selectedList = [],

    listHeaders = [],
    listTitle = '',
    selectedListHeaders,
    selectedListTitle = '',
    selectedActions = [],
    maxHeightTable = '55vh',

    filterField,
    filterFunction = list => list,
    defaultFilter = {},
    nbPerPage,
}) => {
    const [filter, setFilter] = useState(defaultFilter)

    const listDataIndexed = useListIndexed(listData)

    const listDataId = useMemo(() => listData.map(d => d.id), [listData])

    const addAll = listElem => {
        const listId = listElem.map(({ id }) => id)
        onChange([...selectedList, ...listId])
    }

    const deleteAll = () => {
        onChange([])
    }

    const onAdd = elem => {
        onChange([...selectedList, elem.id])
    }

    const onDelete = elem => {
        onChange(selectedList.filter(id => id !== elem.id))
    }

    const unselectedListFormated = useMemo(() => {
        const unselectedList = difference(listDataId, selectedList)
        return unselectedList.map(id => listDataIndexed[id])
    }, [listDataId, listDataIndexed, selectedList])
    const unselectedListFiltered = useMemo(() => filterFunction(unselectedListFormated, filter), [filter, unselectedListFormated])
    const selectedListFormated = useMemo(() => compact(selectedList.map(id => listDataIndexed[id])), [listDataIndexed, selectedList])
    const selectedListFiltered = useMemo(() => filterFunction(selectedListFormated, filter), [filter, selectedListFormated])

    const customListTitle = (
        <>
            <span style={{ fontSize: '1.4rem', fontWeight: 'bold' }}>{listTitle}</span>
            <span style={{ fontSize: '1.4rem', paddingLeft: '5px' }}>({unselectedListFiltered.length}/{unselectedListFormated.length} {unselectedListFormated.length > 1 ? i18n.elements : i18n.element})</span>
        </>
    )

    const customSelectedListTitle = (
        <>
            <span style={{ fontSize: '1.4rem', fontWeight: 'bold' }}>{selectedListTitle}</span>
            <span style={{ fontSize: '1.4rem', paddingLeft: '5px' }}>({selectedListFiltered.length}/{selectedListFormated.length} {selectedListFormated.length > 1 ? i18n.elements : i18n.element})</span>
        </>
    )

    return (
        <Grid container spacing={1}>
            <Grid size={12}>
                {!isNil(filterField) && (
                    <Card>
                        <CardContent>
                            {filterField({ filter, setFilter })}
                        </CardContent>
                    </Card>
                )}
            </Grid>
            <Grid size={12}>
                <SelectionTable
                    maxHeight={maxHeightTable}

                    listData={unselectedListFiltered}
                    listHeaders={listHeaders}
                    listTitle={customListTitle}

                    selectedData={selectedListFiltered}
                    selectedHeaders={selectedListHeaders ?? listHeaders}
                    selectedTitle={customSelectedListTitle}
                    selectedActions={selectedActions}

                    onAdd={onAdd}
                    addAll={addAll}
                    onDelete={onDelete}
                    deleteAll={deleteAll}
                    nbPerPage={nbPerPage}
                />
            </Grid>
        </Grid>
    )
}

SimpleSelectionTable.propTypes = {
    onChange: PropTypes.func.isRequired,

    listData: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number.isRequired,
            // every key from listHeaders and selectedListHeaders should exist in this object
        })),
        PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string.isRequired,
            // every key from listHeaders and selectedListHeaders should exist in this object
        })),
    ]).isRequired,
    selectedList: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.number),
        PropTypes.arrayOf(PropTypes.string),
    ]).isRequired,

    listHeaders: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            key: PropTypes.string.isRequired,
        }),
    ])).isRequired,
    listTitle: PropTypes.string.isRequired,
    selectedListHeaders: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            key: PropTypes.string.isRequired,
        }),
    ])),
    selectedListTitle: PropTypes.string.isRequired,
    selectedActions: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.shape({
            icon: PropTypes.string,
            tooltip: PropTypes.string,
            onClick: PropTypes.func,
            color: PropTypes.string,
            displayed: PropTypes.bool,
            'data-cy': PropTypes.string,
        }),
    ])),
    maxHeightTable: PropTypes.string,

    filterField: PropTypes.func, // can be a function or a component, filter and setFilter are passed in props
    filterFunction: PropTypes.func, // (list, filter) => return filteredList
    defaultFilter: PropTypes.shape({}),
    nbPerPage: PropTypes.arrayOf(PropTypes.shape({})),
}

const SimpleFilterFunction = (list, { searchValue = '' }) => {
    const searchValueFormat = searchAllCharacters(searchValue)
    return searchValue ? list.filter(p => p.labelSearch.includes(searchValueFormat)) : list
}

const SimpleFilterField = ({
    filter = {},
    setFilter = () => { },
}) => {
    const [stateFilter, setStateFilter] = useState(filter)

    useDebounce(() => {
        setFilter(stateFilter)
    }, 500, [stateFilter])

    return (
        <Grid container spacing={1}>
            <Grid size={6}>
                <Input
                    title={i18n.search}
                    value={stateFilter.searchValue}
                    onChange={searchValue => setStateFilter(prev => ({ ...prev, searchValue }))}
                />
            </Grid>
        </Grid>
    )
}

SimpleFilterField.propTypes = {
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
    }),
    setFilter: PropTypes.func,
}

const ParameterFilterField = ({
    filter = {},
    setFilter = () => { },
}) => {
    const [stateFilter, setStateFilter] = useState(filter)

    useDebounce(() => {
        setFilter(stateFilter)
    }, 500, [stateFilter])

    return (
        <Grid container spacing={1} style={{ paddingTop: '5px' }}>
            <Grid size={6}>
                <Input
                    title={i18n.search}
                    value={stateFilter.searchValue}
                    onChange={searchValue => setStateFilter(prev => ({ ...prev, searchValue }))}
                />
            </Grid>
            <Grid size={6}>
                <SelectionSelect
                    value={stateFilter.selection}
                    type={SELECTION.PC}
                    onChange={(listParam, selection) => setStateFilter(prev => ({ ...prev, selection, listParam }))}
                />
            </Grid>
        </Grid>
    )
}

ParameterFilterField.propTypes = {
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
        selection: PropTypes.string,
        listParam: PropTypes.arrayOf(PropTypes.string),
    }),
    setFilter: PropTypes.func,
}

const ActiveParameterFilterField = ({
    filter = {},
    setFilter = () => { },
}) => {
    const [stateFilter, setStateFilter] = useState(filter)

    useDebounce(() => {
        setFilter(stateFilter)
    }, 500, [stateFilter])

    return (
        <Grid container spacing={1} style={{ paddingTop: '5px' }}>
            <Grid size={6}>
                <Input
                    title={i18n.search}
                    value={stateFilter.searchValue}
                    onChange={searchValue => setStateFilter(prev => ({ ...prev, searchValue }))}
                />
            </Grid>
            <Grid size={6}>
                <SelectionSelect
                    value={stateFilter.selection}
                    type={SELECTION.PC}
                    onChange={(listParam, selection) => setStateFilter(prev => ({ ...prev, selection, listParam }))}
                />
            </Grid>
            <Grid size={6}>
                <Checkbox
                    label={i18n.displayNonActiveParameters}
                    checked={stateFilter.shouldDisplayNonActive}
                    onChange={shouldDisplayNonActive => setStateFilter(prev => ({ ...prev, shouldDisplayNonActive }))}
                />
            </Grid>
        </Grid>
    )
}

ActiveParameterFilterField.propTypes = {
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
        selection: PropTypes.string,
        listParam: PropTypes.arrayOf(PropTypes.string),
        shouldDisplayActive: PropTypes.bool,
    }),
    setFilter: PropTypes.func,
}

const TaxonFilterField = ({
    filter = {},
    setFilter = () => { },
}) => {
    const [stateFilter, setStateFilter] = useState(filter)

    useDebounce(() => {
        setFilter(stateFilter)
    }, 500, [stateFilter])

    return (
        <Grid container spacing={1} style={{ paddingTop: '5px' }}>
            <Grid size={6}>
                <Input
                    title={i18n.search}
                    value={stateFilter.searchValue}
                    onChange={searchValue => setStateFilter(prev => ({ ...prev, searchValue }))}
                />
            </Grid>
            <Grid size={6}>
                <SelectionSelect
                    value={stateFilter.selection}
                    type={SELECTION.TAXON}
                    onChange={(listTaxon, selection) => setStateFilter(prev => ({ ...prev, selection, listTaxon }))}
                />
            </Grid>
        </Grid>
    )
}

TaxonFilterField.propTypes = {
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
        selection: PropTypes.string,
        listTaxon: PropTypes.arrayOf(PropTypes.string),
    }),
    setFilter: PropTypes.func,
}

const QualitometerFilterField = ({
    filter = {},
    setFilter = () => { },
}) => {
    const {
        qualitometers,
        cities,
        networks,
        accountUser,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        cities: store.CityReducer.cities,
        networks: store.NetworkReducer.networks,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const [stateFilter, setStateFilter] = useState(filter)

    useDebounce(() => {
        setFilter(stateFilter)
    }, 500, [stateFilter])

    return (
        <Grid container columnSpacing={2} rowSpacing={1} style={{ paddingTop: '5px' }}>
            <Grid size={6}>
                <Input
                    title={i18n.search}
                    value={stateFilter.searchValue}
                    onChange={searchValue => setStateFilter(prev => ({ ...prev, searchValue }))}
                />
            </Grid>
            <Grid size={6}>
                <SimpleFilterSelect
                    stationType={STATION_TYPE_NAME.quality}
                    onChange={(resultFilter, newFilter) => {
                        setStateFilter(prev => ({ ...prev, resultFilter, filter: newFilter }))
                    }}
                    stations={qualitometers}
                />
            </Grid>
            <Grid size={6}>
                <Select
                    label={i18n.city}
                    value={stateFilter.city}
                    options={cities}
                    onChange={city => setStateFilter(prev => ({ ...prev, city }))}
                    keyLabel={showInseeCode ? 'labelWithCode' : 'name'}
                />
            </Grid>
            <Grid size={6}>
                <Select
                    label={i18n.network}
                    value={stateFilter.network}
                    options={authorizeFilter(networks, accountUser, stateFilter.network)}
                    onChange={network => setStateFilter(prev => ({ ...prev, network }))}
                    clearFunction
                />
            </Grid>
        </Grid>
    )
}

QualitometerFilterField.propTypes = {
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
        resultFilter: PropTypes.arrayOf(PropTypes.number),
        filter: PropTypes.number,
        city: PropTypes.string,
    }),
    setFilter: PropTypes.func,
}

const PiezometerFilterField = ({
    filter = {},
    setFilter = () => { },
}) => {
    const {
        piezometers,
        cities,
    } = useSelector(store => ({
        piezometers: store.PiezometryReducer.piezometersLight,
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const [stateFilter, setStateFilter] = useState(filter)

    useDebounce(() => {
        setFilter(stateFilter)
    }, 500, [stateFilter])

    return (
        <Grid container columnSpacing={2} rowSpacing={1} style={{ paddingTop: '5px' }}>
            <Grid size={6}>
                <Input
                    title={i18n.search}
                    value={stateFilter.searchValue}
                    onChange={searchValue => setStateFilter(prev => ({ ...prev, searchValue }))}
                />
            </Grid>
            <Grid size={6}>
                <SimpleFilterSelect
                    stationType={STATION_TYPE_NAME.piezometry}
                    onChange={(resultFilter, newFilter) => {
                        setStateFilter(prev => ({ ...prev, resultFilter, filter: newFilter }))
                    }}
                    stations={piezometers}
                />
            </Grid>
            <Grid size={6}>
                <Select
                    label={i18n.city}
                    value={stateFilter.city}
                    options={cities}
                    onChange={city => setStateFilter(prev => ({ ...prev, city }))}
                    keyLabel='labelWithCode'
                />
            </Grid>
        </Grid>
    )
}

PiezometerFilterField.propTypes = {
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
        resultFilter: PropTypes.arrayOf(PropTypes.number),
        filter: PropTypes.number,
        city: PropTypes.string,
    }),
    setFilter: PropTypes.func,
}

export default SimpleSelectionTable
export {
    SimpleFilterFunction,
    SimpleFilterField,

    ParameterFilterField,
    ActiveParameterFilterField,
    TaxonFilterField,
    QualitometerFilterField,
    PiezometerFilterField,
}